import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import { useConfig } from '../context/ConfigContext';

const EventGallery = () => {
    const [images, setImages] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedImage, setSelectedImage] = useState(null);
    const { config } = useConfig();

    // Get configuration for the main gallery page
    const galleryConfig = config?.pageDesigns?.gallery || { enabled: true, type: 'upload' };

    useEffect(() => {
        if (galleryConfig.type === 'upload') {
            fetchGallery();
        } else {
            setIsLoading(false);
        }
    }, [galleryConfig.type]);

    // Embed Mode: Occupies full screen
    if (galleryConfig.type === 'embed' && galleryConfig.url) {
        return (
            <div className="w-full h-screen bg-black animate-fadeIn overflow-hidden relative z-0" style={{ WebkitOverflowScrolling: 'touch', overflowY: 'auto' }}>
                <iframe 
                    src={galleryConfig.url} 
                    className="w-full h-full border-none"
                    title="Galería del Evento"
                    allowFullScreen
                    scrolling="yes"
                />
            </div>
        );
    }

    const fetchGallery = async () => {
        try {
            const response = await api.get('/gallery');
            setImages(response.data);
        } catch (error) {
            console.error("Error fetching gallery", error);
        } finally {
            setIsLoading(false);
        }
    };

    // Group images by upload_date
    const groupedImages = images.reduce((groups, img) => {
        const date = new Date(img.upload_date).toLocaleDateString('es-ES', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
        if (!groups[date]) groups[date] = [];
        groups[date].push(img);
        return groups;
    }, {});

    if (isLoading) {
        return (
            <div className="flex flex-col items-center justify-center py-40">
                <div className="w-12 h-12 border-4 border-white/10 border-t-purple-600 rounded-full animate-spin"></div>
                <p className="text-white/20 text-[10px] font-black uppercase tracking-widest mt-6">Sincronizando Galería Principal</p>
            </div>
        );
    }

    // Embed Mode for Main Gallery
    if (galleryConfig.type === 'embed') {
        return (
            <div className="animate-fadeIn w-full -mt-4 lg:-mt-10 pb-10">
                <header className="mb-8 text-center px-4">
                    <h1 className="text-4xl font-black text-white tracking-tighter italic uppercase">Galería del Evento</h1>
                    <div className="w-20 h-1 bg-purple-600 mx-auto mt-4 rounded-full shadow-lg shadow-purple-900/50"></div>
                </header>
                <div className="w-full h-[75vh] min-h-[500px] rounded-[40px] overflow-hidden border border-white/10 shadow-3xl bg-black/20 backdrop-blur-sm">
                    <iframe 
                        src={galleryConfig.url} 
                        className="w-full h-full border-none"
                        title="Galería Embebida"
                        allowFullScreen
                    />
                </div>
            </div>
        );
    }

    return (
        <div className="animate-fadeIn pb-20 px-4">
            <header className="mb-12 text-center">
                <div className="inline-block p-6 bg-white/5 backdrop-blur-xl rounded-[35px] border border-white/10 text-4xl mb-6 shadow-2xl">
                    🖼️
                </div>
                <h1 className="text-4xl sm:text-5xl font-black text-white tracking-tighter italic uppercase">Galería del Evento</h1>
                <p className="text-white/40 font-bold mt-3 uppercase tracking-widest text-[10px]">Momentos capturados para revivir la experiencia NeuroUpdate.</p>
            </header>

            {Object.keys(groupedImages).length > 0 ? (
                Object.entries(groupedImages).map(([date, imgs]) => (
                    <section key={date} className="mb-20">
                        <div className="flex items-center gap-6 mb-10">
                            <h2 className="text-xs font-black text-white uppercase tracking-[0.3em] bg-purple-600 px-8 py-3 rounded-full shadow-xl">
                                {date}
                            </h2>
                            <div className="h-[1px] flex-1 bg-gradient-to-r from-white/10 to-transparent"></div>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8">
                            {imgs.map((img) => (
                                <div 
                                    key={img.id}
                                    onClick={() => setSelectedImage(img)}
                                    className="group relative cursor-pointer overflow-hidden rounded-[40px] border border-white/5 bg-white/5 backdrop-blur-md shadow-xl hover:shadow-purple-500/20 transition-all hover:scale-[1.05] active:scale-95 duration-500"
                                >
                                    <div className="aspect-square overflow-hidden">
                                        <img 
                                            src={img.display_url} 
                                            alt={img.original_name} 
                                            className="w-full h-full object-cover transform transition-transform duration-1000 group-hover:scale-110"
                                            loading="lazy"
                                        />
                                    </div>
                                    <div className="absolute inset-0 bg-gradient-to-t from-purple-900/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-center p-6">
                                        <span className="text-white text-[9px] font-black uppercase tracking-widest bg-purple-600 px-5 py-2 rounded-full shadow-lg">Ver +</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>
                ))
            ) : (
                <div className="text-center py-32 bg-white/5 backdrop-blur-sm rounded-[50px] border-2 border-dashed border-white/10 p-10 mt-10 animate-pulse">
                    <span className="text-6xl grayscale opacity-10">📸</span>
                    <p className="text-white/20 font-black mt-6 text-[10px] uppercase tracking-[0.4em]">Aún no hay imágenes compartidas</p>
                </div>
            )}

            {/* Premium Lightbox */}
            {selectedImage && (
                <div 
                    className="fixed inset-0 z-[100] bg-[#0c001e]/98 backdrop-blur-2xl flex items-center justify-center p-4 sm:p-10 animate-fadeIn"
                    onClick={() => setSelectedImage(null)}
                >
                    <button 
                        className="absolute top-8 right-8 w-12 h-12 bg-white/5 hover:bg-white/10 text-white rounded-full flex items-center justify-center text-2xl transition-all hover:rotate-90"
                        onClick={(e) => { e.stopPropagation(); setSelectedImage(null); }}
                    >
                        ✕
                    </button>
                    <div className="max-w-6xl w-full flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
                        <div className="relative rounded-[40px] overflow-hidden shadow-[0_0_80px_rgba(139,92,246,0.3)] border-4 border-white/5 animate-scaleIn">
                            <img 
                                src={selectedImage.display_url} 
                                alt="Preview" 
                                className="max-h-[85vh] w-auto object-contain"
                            />
                        </div>
                        <div className="mt-8 flex gap-6">
                            <a 
                                href={selectedImage.display_url} 
                                download 
                                className="bg-purple-600 px-10 py-5 rounded-full shadow-2xl border border-white/20 text-white font-black uppercase tracking-[0.2em] text-[10px] hover:scale-105 hover:bg-purple-500 transition-all flex items-center gap-3"
                            >
                                Descargar Original 💾
                            </a>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EventGallery;
